import { getResumesFromCache } from 'src/components/Profile/Resume/data';
import {
  Actions as ResumeActions,
  type ResumeAction,
} from 'src/components/Profile/Resume/reducer';
import type { Origin } from 'src/components/Profile/Resume/types';
import {
  Actions as UnconfirmedDataActions,
  type UnconfirmedDataAction,
} from 'src/components/Profile/UnconfirmedData/actionCreators';
import { type RouterAction, UPDATE_ROUTE } from 'src/components/Router/reducer';
import type { Analytics } from 'src/middlewares/createAnalyticsMiddleware';
import { createParsedData } from 'src/modules/analytics/parsedData';
import type {
  AnalyticsActionOrigin,
  AnalyticsResumeType,
} from 'src/modules/analytics/types';
import type { ReduxInjections, Action, ApiClient } from 'src/types';

import { instrumentAnalytics } from '../analyticsMiddlewareUtils';

const currentPageSection = 'resume';

export const resumeTypes: {
  [key in ResumeActions]?: AnalyticsResumeType;
} = {
  RESUME_CREATE_START: 'generated',
  RESUME_CREATE_SUCCESS: 'generated',
  RESUME_UPLOAD_START: 'uploaded',
  RESUME_UPLOAD_SUCCESS: 'uploaded',
} as const;

export const profileActionOriginMap: {
  [key in ResumeActions]?: AnalyticsActionOrigin;
} = {
  RESUME_UPLOAD_START: 'manage resumes',
  RESUME_CREATE_START: 'generate resume prompt',
  RESUME_UPLOAD_SUCCESS: 'main card',
  RESUME_CREATE_SUCCESS: 'main card',
  RESUME_DELETE_START: 'main card',
} as const;

export const createResumeAnalyticsMiddleware = (
  analytics: Analytics,
  { apolloClient }: ApiClient,
) =>
  instrumentAnalytics(analytics) &&
  (({}: ReduxInjections) =>
    (next: (action: Action) => void) =>
    (action: ResumeAction | UnconfirmedDataAction | RouterAction) => {
      const { meta } = action;
      const metaOrigin: Origin = meta?.origin ?? '';

      const { resumeUpdated, resumeParsedDataLinkClicked, getState } =
        analytics;

      switch (action.type) {
        case UPDATE_ROUTE: {
          const { to, analytics: { actionName } = {} } = action.payload;

          if (actionName === 'resume_parsing_new_info_pressed') {
            resumeParsedDataLinkClicked({
              eventName: 'resume_parsing_new_info_pressed',
              parsedDataSection:
                to === 'career-history' ? 'career history' : 'education',
              currentPageSection: 'resume',
            });
          }
          break;
        }

        case ResumeActions.RESUME_UPLOAD_START:
        case ResumeActions.RESUME_CREATE_START: {
          const { isDefault: isResumeDefault } = action.payload;
          const resumeType = resumeTypes[action.type];
          const linkPosition = profileActionOriginMap[action.type];

          resumeUpdated({
            currentPageSection,
            eventName: 'resume_upload_pressed',
            resumeType,
            isResumeDefault: isResumeDefault ?? false,
            linkPosition,
          });

          break;
        }

        case ResumeActions.RESUME_UPLOAD_SUCCESS:
        case ResumeActions.RESUME_CREATE_SUCCESS: {
          const { isDefault: isResumeDefault } = action.payload;
          const resumeType = resumeTypes[action.type];
          resumeUpdated({
            currentPageSection,
            eventName: 'profile_update_succeeded',
            resumeType,
            isResumeDefault: isResumeDefault ?? false,
            updateType: 'add',
            actionOrigin:
              getState().actionOrigin || profileActionOriginMap[action.type],
          });

          break;
        }

        case ResumeActions.RESUME_DELETE_START: {
          const { id } = action.payload;
          const { resumes } = getResumesFromCache({
            apolloClient,
          });
          const isResumeDefault =
            resumes.find((resume) => resume.id === id)?.isDefault ?? false;
          const resumeType =
            metaOrigin === 'Generated' ? 'generated' : 'uploaded';

          resumeUpdated({
            currentPageSection,
            eventName: 'profile_update_succeeded',
            resumeType,
            isResumeDefault,
            updateType: 'delete',
            actionOrigin: profileActionOriginMap[action.type],
          });
          break;
        }

        case ResumeActions.RESUME_GET_LINK_START: {
          const id = action.payload;
          const { resumes } = getResumesFromCache({
            apolloClient,
          });
          const isResumeDefault =
            resumes.find((resume) => resume.id === id)?.isDefault ?? false;
          const resumeType =
            metaOrigin === 'Generated' ? 'generated' : 'uploaded';
          resumeUpdated({
            currentPageSection,
            eventName: 'resume_download_succeeded',
            linkPosition: 'main card',
            resumeType,
            isResumeDefault,
          });
          break;
        }

        case ResumeActions.RESUME_UPDATE_DEFAULT_START: {
          const resumeType =
            metaOrigin === 'Generated' ? 'generated' : 'uploaded';
          resumeUpdated({
            currentPageSection,
            eventName: 'resume_make_default_succeeded',
            resumeType,
            isResumeDefault: true,
          });
          break;
        }

        case UnconfirmedDataActions.FETCH_UNCONFIRMED_DATA_SUCCESS: {
          const { roles, qualifications } = action.payload;

          resumeUpdated({
            currentPageSection,
            eventName: 'resume_parsing_succeeded',
            parsedData: createParsedData({
              rolesCount: roles.length,
              qualificationsCount: qualifications.length,
            }),
          });
          break;
        }

        default: {
          break;
        }
      }

      return next(action);
    });
